import React from "react";
import { Heading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { useVisit } from "../../contexts/VisitContext";
import { Avatar } from "../../design/avatar";
import moment from "moment";
import CreateVisitButton from "../../components/CreateVisitButton";
import VisitStatusBadge from "../../components/VisitStatusBadge";
import { useCase } from "../../contexts/CaseContext";
import Toolbar from "../VisitListPage/components/Toolbar";
import { getInitials } from "../../utils/string";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import EmptyStateView from "../../components/EmptyStateView";

/**
 * Admin visits page - shows all visits with full management capabilities
 * Re-exports the existing VisitListPage functionality
 */
const AdminVisits: React.FC = () => {
  const { visits, loading, error } = useVisit();
  const { cases } = useCase();

  if (loading.list) {
    return <LoadingView />;
  }

  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  return (
    <>
      <div className="flex items-end justify-between gap-4">
        <Heading>Visits</Heading>
        <CreateVisitButton />
      </div>
      <Toolbar />
      {loading.list && <LoadingView />}
      {visits.length === 0 && (
        <EmptyStateView
          title="No Visits found"
          message="Create a new visit to get started"
        />
      )}
      {visits.length > 0 && (
        <Table className="mt-8 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Monitor</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>Case</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {visits.map((visit) => {
              const duration =
                visit.endDate.toDate().getTime() -
                visit.startDate.toDate().getTime();
              return (
                <TableRow key={visit.id} href={`/visit/${visit.id}`}>
                  <TableCell>
                    {visit.supervisor && (
                      <div className="flex items-center gap-2">
                        <Avatar
                          src={visit.supervisor.avatarUrl}
                          className="size-6"
                          initials={getInitials(
                            `${visit.supervisor.firstName} ${visit.supervisor.lastName}`,
                          )}
                        />
                        <span>{`${visit.supervisor.firstName} ${visit.supervisor.lastName}`}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {visit.startDate &&
                      moment(visit.startDate.toDate()).format("llll")}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const totalMin = Math.round(duration / 1000 / 60);
                      const h = Math.floor(totalMin / 60);
                      const m = totalMin % 60;
                      return h > 0 ? `${h}h ${m}m` : `${m}m`;
                    })()}
                  </TableCell>
                  <TableCell>
                    {cases.find((c) => c.id === visit.caseId)?.identifier}
                  </TableCell>
                  <TableCell>
                    <VisitStatusBadge status={visit.status} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
};

export default AdminVisits;
